import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  EllipsisVerticalIcon,
  EyeIcon,
  UserIcon,
  XCircleIcon,
  DocumentDuplicateIcon,
  ArrowTopRightOnSquareIcon,
  ShieldCheckIcon,
  CheckIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  CameraIcon,
} from '@heroicons/react/24/outline';
import toast, { Toaster } from 'react-hot-toast';
import {
  fetchDoctorClinicProfile,
  deleteDoctorClinicSpecialityFee,
  updateDoctorClinicProfilePicture,
  fetchDoctorDigitalCardUrl,
} from '../../../services/MasterAdmin/doctorClinicService';
import {
  DoctorClinic,
  DoctorClinicBranch,
  DoctorClinicBranchTimeslot,
  SpecialityFeeTableRowDto,
  SpecialityWiseFeePair,
  SpecialityWiseFeeRow,
  VendorDocument,
  VendorPolicy,
} from '../../../models/MasterAdmin/doctorClinic';
import FilePreview from '../../../components/MasterAdmin/FilePreview';
import AdminUploadDocumentModal from '../../../components/MasterAdmin/AdminUploadDocumentModal';
import DoctorClinicSpecialityFeeEditorModal from '../../../components/MasterAdmin/DoctorClinicSpecialityFeeEditorModal';
import ConfirmDialog from '../../../components/MasterAdmin/ui/ConfirmDialog';
import { setVendorDocumentVerification, updateDocumentExpiryDate } from '../../../services/vendorDocumentsService';
import { fetchVendorSpecialities, SpecialityOption } from '../../../services/MasterAdmin/doctorClinicTimeslotService';
import { formatDoctorName, formatDoctorSpecialities } from '../../../utils/doctorName';
import { sortConsultationDays } from '../../../utils/consultationDaysOrder';
import VendorPushNotificationLauncher from '../../../components/MasterAdmin/VendorPushNotificationLauncher';
import DoctorClinicWhatsAppSendModal from '../../../components/MasterAdmin/DoctorClinicWhatsAppSendModal';

type FileItem = { url: string; name: string };

const toStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string' && v.trim().length > 0);
  if (typeof value === 'string' && value.trim()) return [value.trim()];
  return [];
};

const toFileArray = (value: unknown): FileItem[] => {
  if (value === null || value === undefined) return [];
  if (typeof value === 'string') {
    const t = value.trim();
    if (!t) return [];
    if (t.startsWith('[') || t.startsWith('{')) {
      try {
        return toFileArray(JSON.parse(t));
      } catch {
        return [{ url: t, name: 'File 1' }];
      }
    }
    return [{ url: t, name: 'File 1' }];
  }
  if (Array.isArray(value)) {
    return value
      .map((item, index) => {
        if (typeof item === 'string' && item.trim()) return { url: item, name: `File ${index + 1}` };
        if (item && typeof item === 'object') {
          const raw = item as Record<string, unknown>;
          const url = typeof raw.url === 'string' ? raw.url : '';
          if (!url) return null;
          return { url, name: typeof raw.name === 'string' && raw.name.trim() ? raw.name : `File ${index + 1}` };
        }
        return null;
      })
      .filter((item): item is FileItem => !!item);
  }
  if (value && typeof value === 'object') {
    const raw = value as Record<string, unknown>;
    const url = typeof raw.url === 'string' ? raw.url : '';
    if (url) return [{ url, name: typeof raw.name === 'string' && raw.name.trim() ? raw.name : 'File 1' }];
  }
  return [];
};

const formatDateTime = (value: unknown): string => {
  if (!value) return 'N/A';
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleString();
};

const formatPrimitive = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return 'N/A';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

const formatAddressLine = (d: DoctorClinic): string => {
  const parts = [d.address, d.city, d.state]
    .map((p) => (p == null ? '' : String(p).trim()))
    .filter((s) => s.length > 0);
  const pin = d.pincode == null ? '' : String(d.pincode).trim();
  if (parts.length === 0 && pin.length === 0) return 'N/A';
  const head = parts.join(', ');
  if (pin.length === 0) return head;
  return head.length > 0 ? `${head} - ${pin}` : pin;
};

const formatRupeeFee = (value: string | undefined): string => {
  const t = (value ?? '').trim();
  return t.length > 0 ? `₹${t}` : 'N/A';
};

const formatFeePairCell = (pair?: SpecialityWiseFeePair): string => {
  if (!pair || (!pair.firstVisitFees.trim() && !pair.followUpFees.trim())) return 'N/A';
  return `First ${formatRupeeFee(pair.firstVisitFees)} · Follow-up ${formatRupeeFee(pair.followUpFees)}`;
};

const hasConsultationType = (types: string[], needle: string) =>
  types.some((t) => t.toLowerCase().includes(needle));

type SpecialityFeeTableRow = {
  speciality: string;
  channelLabel: string;
  firstVisitFees: string;
  followUpFees: string;
};

const pickFeePair = (m: unknown): SpecialityWiseFeePair => {
  if (!m || typeof m !== 'object' || Array.isArray(m)) {
    return { firstVisitFees: '', followUpFees: '' };
  }
  const o = m as Record<string, unknown>;
  return {
    firstVisitFees: String(
      o.firstVisitFees ??
      o.firstVisitFee ??
      o.firstvisitFees ??
      o.firstConsultationFees ??
      o.firstConsultationFee ??
      o.firstTimeVisitFees ??
      ''
    ).trim(),
    followUpFees: String(o.followUpFees ?? o.followupFees ?? '').trim(),
  };
};

/** One table row per speciality (flat) or per speciality + Online/In-clinic channel. */
const expandSpecialityFeesForTable = (rows: SpecialityWiseFeeRow[]): SpecialityFeeTableRow[] => {
  const expanded: SpecialityFeeTableRow[] = [];
  for (const row of rows) {
    const speciality = row.speciality.trim() || 'N/A';
    const btc = row.feesByConsultationType;
    const channels: { label: string; pair?: SpecialityWiseFeePair }[] = [
      { label: 'Online', pair: btc?.online },
      { label: 'In-clinic', pair: btc?.offline },
    ];
    let addedChannel = false;
    if (btc && (btc.online || btc.offline)) {
      for (const { label, pair } of channels) {
        if (!pair || (!pair.firstVisitFees.trim() && !pair.followUpFees.trim())) continue;
        expanded.push({
          speciality,
          channelLabel: label,
          firstVisitFees: pair.firstVisitFees,
          followUpFees: pair.followUpFees,
        });
        addedChannel = true;
      }
    }
    if (addedChannel) continue;
    const first = String(row.firstVisitFees ?? '').trim();
    const follow = String(row.followUpFees ?? '').trim();
    if (!first && !follow) continue;
    expanded.push({
      speciality,
      channelLabel: '',
      firstVisitFees: first,
      followUpFees: follow,
    });
  }
  return expanded;
};

const formatFeeLine = (
  label: string,
  p: SpecialityWiseFeePair | undefined
): React.ReactNode => {
  if (!p || (!p.firstVisitFees.trim() && !p.followUpFees.trim())) {
    return (
      <span className="text-gray-500 dark:text-gray-400">
        {label}: N/A
      </span>
    );
  }
  return (
    <span>
      {label}: First consultation {formatRupeeFee(p.firstVisitFees)} · Follow-up{' '}
      {formatRupeeFee(p.followUpFees)}
    </span>
  );
};

/** Legacy single-cell renderer (specialityWiseFees rows). Table view uses expandSpecialityFeesForTable. */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const formatSpecialityFeesCell = (
  row: SpecialityWiseFeeRow | SpecialityFeeTableRow
): React.ReactNode => {
  if (
    'channelLabel' in row &&
    'firstVisitFees' in row &&
    !('feesByConsultationType' in row && row.feesByConsultationType)
  ) {
    const tableRow = row as SpecialityFeeTableRow;
    return (
      <span className="text-sm text-gray-700 dark:text-gray-300">
        {tableRow.channelLabel ? `${tableRow.channelLabel}: ` : ''}
        First consultation {formatRupeeFee(tableRow.firstVisitFees)} · Follow-up{' '}
        {formatRupeeFee(tableRow.followUpFees)}
      </span>
    );
  }
  const feeRow = row as SpecialityWiseFeeRow;
  const btc = feeRow.feesByConsultationType;
  if (btc && (btc.online || btc.offline)) {
    return (
      <div className="space-y-1.5 text-gray-700 dark:text-gray-300">
        <div className="text-sm">{formatFeeLine('Online', btc.online)}</div>
        <div className="text-sm">{formatFeeLine('In-clinic', btc.offline)}</div>
      </div>
    );
  }
  return (
    <span className="text-sm text-gray-700 dark:text-gray-300">
      First consultation {formatRupeeFee(feeRow.firstVisitFees)} · Follow-up{' '}
      {formatRupeeFee(feeRow.followUpFees)}
    </span>
  );
};

const mapRawSpecialityFeeRow = (item: unknown): SpecialityWiseFeeRow | null => {
  if (!item || typeof item !== 'object') return null;
  const raw = item as Record<string, unknown>;
  const speciality = String(raw.speciality ?? '').trim();
  const specialityId =
    typeof raw.specialityId === 'string' && raw.specialityId.trim()
      ? raw.specialityId.trim()
      : null;
  if (!speciality && !specialityId) return null;
  const row: SpecialityWiseFeeRow = {
    speciality: speciality || specialityId || 'N/A',
    specialityId,
  };
  const btc = raw.feesByConsultationType;
  if (btc && typeof btc === 'object' && !Array.isArray(btc)) {
    const b = btc as Record<string, unknown>;
    row.feesByConsultationType = {
      online: pickFeePair(b.online ?? b.Online),
      offline: pickFeePair(b.offline ?? b.Offline),
    };
  }
  const legacyFirst = String(raw.firstVisitFees ?? raw.firstVisitFee ?? '').trim();
  const legacyFollow = String(raw.followUpFees ?? raw.followupFees ?? '').trim();
  if (legacyFirst || legacyFollow) {
    row.firstVisitFees = legacyFirst;
    row.followUpFees = legacyFollow;
  }
  return row;
};

/** Table-backed fee rows only (`DoctorClinicSpecialityFees`). */
const toSpecialityWiseFeesArray = (clinic: DoctorClinic | null | undefined): SpecialityWiseFeeRow[] => {
  const tableRows = clinic?.specialityFeeRows ?? [];
  return tableRows
    .map((item) => mapRawSpecialityFeeRow(item))
    .filter((row): row is SpecialityWiseFeeRow => row !== null);
};

const normalizeDocumentStatus = (
  status: unknown,
  fallback: VendorDocument['status']
): VendorDocument['status'] => {
  if (status === 'pending' || status === 'approved' || status === 'rejected') {
    return status;
  }
  return fallback;
};

const ValueRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => {
  let display: React.ReactNode;
  if (value === null || value === undefined) {
    display = 'N/A';
  } else if (typeof value === 'string') {
    display = value.trim() === '' ? 'N/A' : value;
  } else {
    display = value;
  }
  return (
    <div>
      <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
      <div className="mt-1 text-sm font-medium text-gray-900 dark:text-white">{display}</div>
    </div>
  );
};

/** Docs that can have an expiry date edited by admin */
const canEditExpiry = (doc: VendorDocument): boolean => {
  const type = String(doc.documentType || '').toUpperCase();
  if (type === 'REGISTRATION_CERTIFICATE') return true;
  if (type === 'IDENTITY_PROOF') {
    // Driving License expires; Aadhar/PAN are lifetime
    const name = String(doc.documentName || '').toLowerCase();
    return name.includes('driving') || name.includes('driver') || name.includes('license');
  }
  return false;
};

const DocActionMenu: React.FC<{
  doc: VendorDocument;
  getAnchorEl: () => HTMLButtonElement | null;
  open: boolean;
  loading: boolean;
  onView: () => void;
  onReplace: () => void;
  onVerifyToggle: () => void;
  onEditExpiry: () => void;
  onClose: () => void;
}> = ({ doc, getAnchorEl, open, loading, onView, onReplace, onVerifyToggle, onEditExpiry, onClose }) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const update = () => {
      const anchor = getAnchorEl();
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const menuWidth = 175;
      setPos({
        top: rect.bottom + 4,
        left: Math.max(8, rect.right - menuWidth),
      });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open, getAnchorEl]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      const anchor = getAnchorEl();
      if (menuRef.current?.contains(target) || anchor?.contains(target)) return;
      onClose();
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open, onClose, getAnchorEl]);

  if (!open || !pos) return null;

  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      style={{ position: 'fixed', top: pos.top, left: pos.left, width: 175 }}
      className="z-[200] overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg ring-1 ring-black/5 dark:border-gray-600 dark:bg-gray-800"
    >
      <button
        type="button"
        role="menuitem"
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
        onClick={onView}
      >
        <EyeIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        View
      </button>
      <button
        type="button"
        role="menuitem"
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700"
        onClick={onReplace}
      >
        <ArrowUpTrayIcon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        Upload / Replace
      </button>
      <button
        type="button"
        role="menuitem"
        disabled={loading}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-60 dark:text-gray-200 dark:hover:bg-gray-700"
        onClick={onVerifyToggle}
      >
        {doc.isVerified ? (
          <XCircleIcon className="h-4 w-4 text-amber-600 dark:text-amber-400" />
        ) : (
          <CheckCircleIcon className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
        )}
        {loading ? 'Updating...' : doc.isVerified ? 'Unverify' : 'Verify'}
      </button>
      {canEditExpiry(doc) && (
        <button
          type="button"
          role="menuitem"
          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-indigo-700 hover:bg-indigo-50 dark:text-indigo-300 dark:hover:bg-indigo-900/20"
          onClick={onEditExpiry}
        >
          <PencilSquareIcon className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
          Edit Expiry Date
        </button>
      )}
    </div>,
    document.body
  );
};

const Chips: React.FC<{ items: string[]; emptyLabel?: string }> = ({ items, emptyLabel = 'N/A' }) => {
  if (items.length === 0) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">{emptyLabel}</p>;
  }
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((item) => (
        <span
          key={item}
          className="px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
        >
          {item}
        </span>
      ))}
    </div>
  );
};

const TimeslotsTable: React.FC<{ timeslots: DoctorClinicBranchTimeslot[] }> = ({ timeslots }) => (
  <div className="overflow-x-auto">
    <table className="min-w-full text-sm">
      <thead>
        <tr className="text-left border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <th className="py-2 px-4 font-medium text-gray-700 dark:text-gray-300">Day</th>
          <th className="py-2 px-4 font-medium text-gray-700 dark:text-gray-300">Speciality</th>
          <th className="py-2 px-4 font-medium text-gray-700 dark:text-gray-300">Time</th>
          <th className="py-2 px-4 font-medium text-gray-700 dark:text-gray-300">Interval</th>
          <th className="py-2 px-4 font-medium text-gray-700 dark:text-gray-300">Status</th>
        </tr>
      </thead>
      <tbody>
        {timeslots.map((slot) => (
          <tr
            key={slot.timeSlotID}
            className="border-b border-gray-100 dark:border-gray-800 last:border-0"
          >
            <td className="py-2 px-4 text-gray-900 dark:text-white">{slot.day || 'N/A'}</td>
            <td className="py-2 px-4 text-gray-700 dark:text-gray-300">
              {slot.speciality?.trim() || '—'}
            </td>
            <td className="py-2 px-4 text-gray-700 dark:text-gray-300">
              {slot.startTime} – {slot.endTime}
            </td>
            <td className="py-2 px-4 text-gray-700 dark:text-gray-300">
              {slot.intervalMinutes != null ? `${slot.intervalMinutes} min` : '—'}
            </td>
            <td className="py-2 px-4">
              <span
                className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${slot.isActive
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                    : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                  }`}
              >
                {slot.isActive ? 'Active' : 'Inactive'}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

interface CouncilLink {
  id: string;
  name: string;
  url: string;
  stateKey?: string;
}

const COUNCIL_LINKS: CouncilLink[] = [
  {
    id: 'nmc',
    name: 'NMC (National Medical Commission)',
    url: 'https://www.nmc.org.in/information-desk/indian-medical-register/',
  },
  {
    id: 'mmc',
    name: 'MMC (Maharashtra Medical Council)',
    url: 'https://maharashtramedicalcouncil.in/frmrmplist.aspx',
    stateKey: 'maharashtra',
  },
  {
    id: 'mdc',
    name: 'MDC (Maharashtra Dental Council)',
    url: 'https://msdcmumbai.in/Frm_Find_RMP.aspx',
    stateKey: 'maharashtra',
  },
  {
    id: 'dmc',
    name: 'DMC (Delhi Medical Council)',
    url: 'https://delhimedicalcouncil.org/home/regi_search',
    stateKey: 'delhi',
  },
  {
    id: 'tnmc',
    name: 'TNMC (Tamil Nadu Medical Council)',
    url: 'https://tamilnadumedicalcouncil.org/rmp/search',
    stateKey: 'tamilnadu',
  },
  {
    id: 'tsmc',
    name: 'TSMC (Telangana Medical Council)',
    url: 'https://regonlinetsmc.in/doctorsearch',
    stateKey: 'telangana',
  },
  {
    id: 'apmc',
    name: 'APMC (Andhra Pradesh Medical Council)',
    url: 'https://apmc.ap.gov.in/medicalregister/',
    stateKey: 'andhrapradesh',
  },
  {
    id: 'ksdc',
    name: 'KSDC (Karnataka State Dental Council)',
    url: 'https://ksdc.in/search-dentist.php?input=',
    stateKey: 'karnataka',
  },
];

const getMatchedStateKey = (councilText: string | undefined | null): string | null => {
  if (!councilText) return null;
  const lower = councilText.toLowerCase();

  if (
    lower.includes('maharashtra') ||
    lower.includes('maharasta') ||
    lower.includes('maha') ||
    lower.includes('msdc') ||
    lower.includes('mmc')
  ) {
    return 'maharashtra';
  }
  if (lower.includes('delhi') || lower.includes('dmc')) {
    return 'delhi';
  }
  if (lower.includes('tamil') || lower.includes('tnmc')) {
    return 'tamilnadu';
  }
  if (lower.includes('telang') || lower.includes('tsmc')) {
    return 'telangana';
  }
  if (lower.includes('andhra') || lower.includes('apmc')) {
    return 'andhrapradesh';
  }
  if (
    lower.includes('karnataka') ||
    lower.includes('ksdc') ||
    lower.includes('kmc') ||
    lower.includes('karnatak')
  ) {
    return 'karnataka';
  }
  return null;
};

const isImage = (url: string): boolean => {
  return /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i.test(url);
};

const isPDF = (url: string): boolean => {
  return /\.(pdf)(\?.*)?$/i.test(url);
};

const DoctorClinicVendorDetailsPage: React.FC = () => {
  const { vendorId } = useParams<{ vendorId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<DoctorClinic | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeDocMenuId, setActiveDocMenuId] = useState<string | null>(null);
  const [documentActionLoadingId, setDocumentActionLoadingId] = useState<string | null>(null);
  const docMenuButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const [verificationOpen, setVerificationOpen] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const verificationRef = useRef<HTMLDivElement>(null);

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [specialityOptions, setSpecialityOptions] = useState<SpecialityOption[]>([]);
  const [feeModalOpen, setFeeModalOpen] = useState(false);
  const [feeModalMode, setFeeModalMode] = useState<'create' | 'edit'>('create');
  const [editingFeeRow, setEditingFeeRow] = useState<SpecialityFeeTableRowDto | null>(null);
  const [feeActionLoadingId, setFeeActionLoadingId] = useState<string | null>(null);
  const [feeDeleteTarget, setFeeDeleteTarget] = useState<SpecialityFeeTableRowDto | null>(null);
  const [whatsappSendOpen, setWhatsappSendOpen] = useState(false);
  const [uploadingProfilePic, setUploadingProfilePic] = useState(false);
  const profilePicInputRef = useRef<HTMLInputElement>(null);
  const [digitalCardUrl, setDigitalCardUrl] = useState<string | null>(null);
  const [digitalCardLoading, setDigitalCardLoading] = useState(false);
  const [cardZoom, setCardZoom] = useState(1);
  const [cardPosition, setCardPosition] = useState({ x: 0, y: 0 });
  const [isDraggingCard, setIsDraggingCard] = useState(false);
  const [dragStartCard, setDragStartCard] = useState({ x: 0, y: 0 });

  const handleViewDigitalCard = async () => {
    if (!vendorId) return;
    setDigitalCardLoading(true);
    const toastId = toast.loading('Generating digital card...');
    try {
      const res = await fetchDoctorDigitalCardUrl(vendorId);
      if (res.success && res.cardUrl) {
        setCardZoom(1);
        setCardPosition({ x: 0, y: 0 });
        setDigitalCardUrl(res.cardUrl);
        toast.success('Digital card generated successfully!', { id: toastId });
      } else {
        toast.error('Failed to get digital card URL', { id: toastId });
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Error generating card', { id: toastId });
    } finally {
      setDigitalCardLoading(false);
    }
  };

  const handleDownloadCard = async () => {
    if (!digitalCardUrl) return;
    try {
      const response = await fetch(digitalCardUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${data?.doctorName || 'Doctor'}_Digital_Card.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download card:', err);
      window.open(digitalCardUrl, '_blank');
    }
  };

  const handleProfilePicChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !vendorId) return;

    e.target.value = '';

    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file (JPEG, PNG, WebP).');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image size must be under 10MB.');
      return;
    }

    setUploadingProfilePic(true);
    const toastId = toast.loading('Uploading profile picture...');

    try {
      const res = await updateDoctorClinicProfilePicture(vendorId, file);
      if (res.success || res.clinic) {
        toast.success('Profile picture updated successfully!', { id: toastId });
        const refreshed = await fetchDoctorClinicProfile(vendorId);
        if (refreshed?.data) {
          setData(refreshed.data);
        }
      } else {
        toast.error(res.message || 'Failed to update profile picture', { id: toastId });
      }
    } catch (err: any) {
      console.error('Error uploading profile picture:', err);
      toast.error(
        err.response?.data?.message || err.message || 'Failed to update profile picture',
        { id: toastId }
      );
    } finally {
      setUploadingProfilePic(false);
    }
  };

  // --- Expiry edit modal ---
  const [expiryEditDoc, setExpiryEditDoc] = useState<VendorDocument | null>(null);
  const [expiryEditDate, setExpiryEditDate] = useState('');
  const [expiryEditIssued, setExpiryEditIssued] = useState('');
  const [expiryIsLifetime, setExpiryIsLifetime] = useState(false);
  const [expiryEditLoading, setExpiryEditLoading] = useState(false);

  const handleOpenExpiryEdit = (doc: VendorDocument) => {
    setExpiryEditDoc(doc);
    setExpiryEditDate(doc.expiryDate || '');
    setExpiryEditIssued(doc.issuedDate || '');
    setExpiryIsLifetime(doc.isLifetime ?? false);
    setActiveDocMenuId(null);
  };

  const handleSaveExpiry = async () => {
    if (!expiryEditDoc) return;
    try {
      setExpiryEditLoading(true);
      const updated = await updateDocumentExpiryDate(expiryEditDoc.documentId, {
        expiryDate: expiryIsLifetime ? null : (expiryEditDate || null),
        issuedDate: expiryEditIssued || null,
        isLifetime: expiryIsLifetime,
      });
      if (updated) {
        setData((prev) => {
          if (!prev) return prev;
          const docs = Array.isArray(prev.vendorDocuments) ? prev.vendorDocuments : [];
          return {
            ...prev,
            vendorDocuments: docs.map((d) =>
              d.documentId === expiryEditDoc.documentId
                ? {
                  ...d,
                  ...updated,
                  status: normalizeDocumentStatus(updated.status, d.status),
                }
                : d
            ),
          };
        });
        toast.success('Expiry date updated.');
      }
      setExpiryEditDoc(null);
    } catch {
      toast.error('Failed to update expiry date.');
    } finally {
      setExpiryEditLoading(false);
    }
  };

  const regDocInfo = useMemo(() => {
    const docs = data?.vendorDocuments || [];
    const doc = docs.find(
      (d) => d.documentType === 'REGISTRATION_CERTIFICATE' || d.documentType === 'MEDICAL_LICENSE'
    );
    return {
      number: doc?.documentNumber || 'N/A',
      issuedDate: doc?.issuedDate || 'N/A',
      url: doc?.documentUrl || null,
    };
  }, [data?.vendorDocuments]);

  const copyToClipboard = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => {
        setCopiedField(null);
      }, 1500);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  useEffect(() => {
    if (!verificationOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      if (verificationRef.current && !verificationRef.current.contains(e.target as Node)) {
        setVerificationOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [verificationOpen]);

  const { recommendedCouncils, otherCouncils } = useMemo(() => {
    if (!data) return { recommendedCouncils: [], otherCouncils: [] };
    const matchedStateKey = getMatchedStateKey(
      data.medicalRegistrationCouncil || data.registrationCouncil
    );

    const recommendedIds = new Set<string>(['nmc']);
    if (matchedStateKey) {
      COUNCIL_LINKS.forEach((c) => {
        if (c.stateKey === matchedStateKey) {
          recommendedIds.add(c.id);
        }
      });
    }

    const recommended = COUNCIL_LINKS.filter((c) => recommendedIds.has(c.id));
    recommended.sort((a, b) => {
      if (a.id === 'nmc') return -1;
      if (b.id === 'nmc') return 1;

      const aIsDental = a.name.toLowerCase().includes('dental');
      const bIsDental = b.name.toLowerCase().includes('dental');
      if (aIsDental && !bIsDental) return 1;
      if (!aIsDental && bIsDental) return -1;

      return a.name.localeCompare(b.name);
    });

    const others = COUNCIL_LINKS.filter((c) => !recommendedIds.has(c.id));
    return { recommendedCouncils: recommended, otherCouncils: others };
  }, [data]);

  useEffect(() => {
    if (!vendorId) return;
    const load = async () => {
      try {
        setLoading(true);
        const response = await fetchDoctorClinicProfile(vendorId);
        setData(response.data);
      } catch (e) {
        setError('Failed to load vendor details.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [vendorId]);

  const reloadProfile = async () => {
    if (!vendorId) return;
    const response = await fetchDoctorClinicProfile(vendorId);
    setData(response.data);
  };

  useEffect(() => {
    if (!vendorId || !data) return;
    const loadSpecialities = async () => {
      try {
        const res = await fetchVendorSpecialities(vendorId);
        const options = Array.isArray(res.specialityOptions) ? res.specialityOptions : [];
        if (options.length > 0) {
          setSpecialityOptions(options);
          return;
        }
      } catch {
        // fall back to profile specializations
      }

      const ids = Array.isArray(data.specialization_ids) ? data.specialization_ids : [];
      const names = Array.isArray(data.specializations) ? data.specializations : [];
      const fromProfile: SpecialityOption[] = ids.map((specialityId, index) => ({
        specialityId,
        speciality: names[index]?.trim() || names.find((n) => n.trim()) || specialityId,
      }));
      setSpecialityOptions(fromProfile);
    };
    loadSpecialities();
  }, [vendorId, data?.specialization_ids, data?.specializations]);

  const {
    educationalQualifications,
    specializations,
    languageProficiency,
    consultationTypes,
    consultationDaysOrdered,
    clinicBranches,
    onlineTimeslots,
    insurancePartners,
    otherFacilities,
    clinicPhotos,
    specialityFeeTableRows,
    showFeeChannelColumn,
    specialityFeeRows,
    showOnlineFeeColumn,
    showOfflineFeeColumn,
  } = useMemo(() => {
    const specialityWiseFees = toSpecialityWiseFeesArray(data);
    const specialityFeeTableRows = expandSpecialityFeesForTable(specialityWiseFees);
    const consultationTypesList = toStringArray(data?.consultationTypes);
    const showOnlineFeeColumn =
      hasConsultationType(consultationTypesList, 'online') || consultationTypesList.length === 0;
    const showOfflineFeeColumn =
      hasConsultationType(consultationTypesList, 'offline') ||
      hasConsultationType(consultationTypesList, 'in-clinic') ||
      hasConsultationType(consultationTypesList, 'in clinic') ||
      consultationTypesList.length === 0;
    return {
      educationalQualifications: toStringArray(data?.educationalQualifications),
      specializations: toStringArray(data?.specializations),
      languageProficiency: toStringArray(data?.languageProficiency),
      consultationTypes: consultationTypesList,
      consultationDaysOrdered: sortConsultationDays(toStringArray(data?.consultationDays)),
      clinicBranches: Array.isArray(data?.clinicBranches) ? (data?.clinicBranches ?? []) : [],
      onlineTimeslots: Array.isArray(data?.onlineTimeslots) ? (data?.onlineTimeslots ?? []) : [],
      insurancePartners: toStringArray(data?.insurancePartners),
      otherFacilities: toStringArray(data?.otherFacilities),
      clinicPhotos: toFileArray(data?.clinicPhotos),
      specialityFeeTableRows,
      showFeeChannelColumn: specialityFeeTableRows.some((r) => r.channelLabel.trim().length > 0),
      specialityFeeRows: Array.isArray(data?.specialityFeeRows) ? data?.specialityFeeRows ?? [] : [],
      showOnlineFeeColumn,
      showOfflineFeeColumn,
    };
  }, [data]);

  const vendorDocuments = useMemo<VendorDocument[]>(
    () => (Array.isArray(data?.vendorDocuments) ? data?.vendorDocuments ?? [] : []),
    [data?.vendorDocuments]
  );

  const acceptedPolicies = useMemo<VendorPolicy[]>(
    () => (Array.isArray(data?.acceptedPolicies) ? data?.acceptedPolicies ?? [] : []),
    [data?.acceptedPolicies]
  );

  const shouldShowMapLocation = useMemo(() => {
    const normalizedTypes = consultationTypes.map((type) => type.trim().toLowerCase());
    return normalizedTypes.includes('offline');
  }, [consultationTypes]);

  const handleViewDocument = (url?: string | null) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const openAddFeeModal = () => {
    setFeeModalMode('create');
    setEditingFeeRow(null);
    setFeeModalOpen(true);
  };

  const openEditFeeModal = (row: SpecialityFeeTableRowDto) => {
    setFeeModalMode('edit');
    setEditingFeeRow(row);
    setFeeModalOpen(true);
  };

  const handleDeleteFee = (row: SpecialityFeeTableRowDto) => {
    if (!row.id) return;
    setFeeDeleteTarget(row);
  };

  const confirmDeleteFee = async () => {
    if (!vendorId || !feeDeleteTarget?.id) return;
    try {
      setFeeActionLoadingId(feeDeleteTarget.id);
      await deleteDoctorClinicSpecialityFee(vendorId, feeDeleteTarget.id);
      toast.success('Speciality fees deleted.');
      setFeeDeleteTarget(null);
      await reloadProfile();
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || 'Failed to delete speciality fees.';
      toast.error(message);
    } finally {
      setFeeActionLoadingId(null);
    }
  };

  const handleVerifyToggle = async (doc: VendorDocument) => {
    try {
      setDocumentActionLoadingId(doc.documentId);
      const nextVerify = !doc.isVerified;
      const updatedDoc = await setVendorDocumentVerification(doc.documentId, nextVerify);
      if (!updatedDoc) return;

      setData((prev) => {
        if (!prev) return prev;
        const currentDocs = Array.isArray(prev.vendorDocuments) ? prev.vendorDocuments : [];
        return {
          ...prev,
          vendorDocuments: currentDocs.map((item) =>
            item.documentId === doc.documentId
              ? {
                ...item,
                ...updatedDoc,
                status: normalizeDocumentStatus(updatedDoc.status, item.status),
              }
              : item
          ),
        };
      });
    } catch {
      setError('Failed to update document verification status.');
    } finally {
      setDocumentActionLoadingId(null);
      setActiveDocMenuId(null);
    }
  };

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedDocForEdit, setSelectedDocForEdit] = useState<VendorDocument | null>(null);

  const handleOpenAddModal = () => {
    setSelectedDocForEdit(null);
    setIsUploadModalOpen(true);
  };

  const handleOpenReplaceModal = (doc: VendorDocument) => {
    setSelectedDocForEdit(doc);
    setIsUploadModalOpen(true);
    setActiveDocMenuId(null);
  };

  const handleDocumentUploadSuccess = (updatedDoc: any) => {
    setData((prev) => {
      if (!prev) return prev;
      const currentDocs = Array.isArray(prev.vendorDocuments) ? prev.vendorDocuments : [];
      const exists = currentDocs.some((item) => item.documentId === updatedDoc.documentId);
      let nextDocs: VendorDocument[];
      if (exists) {
        nextDocs = currentDocs.map((item) =>
          item.documentId === updatedDoc.documentId ? { ...item, ...updatedDoc } : item
        );
      } else {
        nextDocs = [updatedDoc, ...currentDocs];
      }
      return {
        ...prev,
        vendorDocuments: nextDocs,
      };
    });
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[220px]">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600 dark:text-red-400">{error || 'Vendor details not found'}</p>
        <button
          onClick={() => navigate(`/vendors/doctor-clinic/${vendorId}`)}
          className="mt-4 text-blue-600 dark:text-blue-400 hover:underline"
        >
          Back to vendor dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 max-w-6xl mx-auto space-y-4 p-3 sm:space-y-6 sm:p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          onClick={() => navigate(`/vendors/doctor-clinic/${vendorId}`)}
          className="inline-flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to vendor dashboard
        </button>
        <div className="flex flex-wrap items-center gap-2">
          {vendorId ? (
            <VendorPushNotificationLauncher
              vendorId={vendorId}
              vendorName={data?.doctorName || data?.clinicName || undefined}
              targetAudience="SINGLE_DOCTOR"
              compact
            />
          ) : null}
          <button
            type="button"
            onClick={() => setWhatsappSendOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-[#25D366]/40 bg-[#25D366]/10 px-3 py-1.5 text-xs font-semibold text-[#128C7E] hover:bg-[#25D366]/20 sm:text-sm dark:border-[#25D366]/30 dark:bg-[#25D366]/10 dark:text-[#25D366]"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            Send WhatsApp
          </button>
          <button
            type="button"
            disabled={digitalCardLoading}
            onClick={() => void handleViewDigitalCard()}
            className="inline-flex items-center gap-1.5 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-500/20 disabled:opacity-50 sm:text-sm dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-500"
          >
            <EyeIcon className="h-4 w-4 shrink-0" />
            {digitalCardLoading ? 'Generating...' : 'View Digital Card'}
          </button>
          <button
            type="button"
            onClick={() => navigate(`/monitoring?tab=intelligence&vendorId=${vendorId}&category=doctor`)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs sm:text-sm font-semibold rounded-lg bg-gradient-to-r from-blue-600 to-cyan-600 text-white hover:from-blue-700 hover:to-cyan-700 active:scale-95 transition-all shadow-sm border border-blue-500/20"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z"
              />
            </svg>
            Analyze ML Intelligence
          </button>
        </div>
      </div>

      <section className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
        <div className="px-6 py-5 border-b dark:border-gray-700 flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="relative group h-20 w-20 shrink-0 rounded-xl overflow-hidden bg-blue-50 dark:bg-blue-900/30 ring-2 ring-blue-100 dark:ring-blue-900/50 flex items-center justify-center">
              {data.profilePicture?.trim() ? (
                <img
                  src={data.profilePicture}
                  alt={data.doctorName || 'Doctor'}
                  className="h-full w-full object-cover"
                />
              ) : (
                <UserIcon className="h-10 w-10 text-blue-600 dark:text-blue-400" />
              )}
              <label
                htmlFor="doctor-profile-pic-input"
                className="absolute inset-0 bg-black/60 text-white flex flex-col items-center justify-center cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                title="Click to upload or edit profile picture"
              >
                <CameraIcon className="h-6 w-6 text-white" />
                <span className="text-[10px] font-semibold mt-0.5">Edit</span>
              </label>
            </div>
            <input
              ref={profilePicInputRef}
              id="doctor-profile-pic-input"
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              className="hidden"
              onChange={handleProfilePicChange}
              disabled={uploadingProfilePic}
            />
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                  {!data.doctorName?.trim() ? 'N/A' : formatDoctorName(data.doctorName)}
                </h1>
                <button
                  type="button"
                  onClick={() => profilePicInputRef.current?.click()}
                  disabled={uploadingProfilePic}
                  className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/40 dark:text-blue-300 dark:hover:bg-blue-900/60 border border-blue-200 dark:border-blue-800 transition-all active:scale-95 disabled:opacity-50"
                  title="Upload or change profile picture"
                >
                  <PencilSquareIcon className="h-3.5 w-3.5" />
                  {uploadingProfilePic ? 'Uploading...' : data.profilePicture?.trim() ? 'Edit Photo' : 'Add Photo'}
                </button>
              </div>
              {(() => {
                const specialitiesText = formatDoctorSpecialities(specializations);
                if (!specialitiesText) return null;
                return (
                  <p className="mt-1 text-sm font-medium text-blue-700 dark:text-blue-300">
                    {specialitiesText}
                  </p>
                );
              })()}
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mt-0.5">
                {!data.clinicName?.trim() ? 'N/A' : data.clinicName}
              </p>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{formatAddressLine(data)}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <span
                  className={`px-2 py-1 text-xs font-semibold rounded-full ${data.vendor.status === 'approved'
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                      : data.vendor.status === 'pending'
                        ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                        : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                    }`}
                >
                  {data.vendor.status.charAt(0).toUpperCase() + data.vendor.status.slice(1)}
                </span>
                <span
                  className={`px-2 py-1 text-xs font-semibold rounded-full ${data.vendor.isActive
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                      : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                    }`}
                >
                  {data.vendor.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>

          {/* Right side: Verification Button & Dropdown */}
          <div className="relative shrink-0" ref={verificationRef}>
            <button
              type="button"
              onClick={() => setVerificationOpen(!verificationOpen)}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-all dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-300 dark:hover:bg-blue-900/30"
            >
              <ShieldCheckIcon className="h-5 w-5" />
              Doctor Verification
            </button>
            {verificationOpen && (
              <div className="absolute right-0 top-full mt-2 z-50 w-80 rounded-2xl border border-gray-200 bg-white p-4 shadow-xl dark:border-gray-700 dark:bg-gray-800 ring-1 ring-black/5 animate-in fade-in duration-200">
                <div className="space-y-3 pb-3 border-b border-gray-100 dark:border-gray-700">
                  <h3 className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Verification Info</h3>

                  <div className="flex items-center justify-between text-sm">
                    <div className="min-w-0 pr-2">
                      <span className="text-gray-500 dark:text-gray-400">Name: </span>
                      <span className="font-semibold text-gray-900 dark:text-white truncate block">{data.doctorName}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(data.doctorName, 'name')}
                      className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors shrink-0"
                      title="Copy Name"
                    >
                      {copiedField === 'name' ? (
                        <CheckIcon className="h-4 w-4 text-green-500 animate-in zoom-in duration-200" />
                      ) : (
                        <DocumentDuplicateIcon className="h-4 w-4" />
                      )}
                    </button>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <div className="min-w-0 pr-2">
                      <span className="text-gray-500 dark:text-gray-400">Reg No: </span>
                      <span className="font-mono font-semibold text-gray-900 dark:text-white block">{regDocInfo.number}</span>
                    </div>
                    {regDocInfo.number !== 'N/A' && (
                      <button
                        type="button"
                        onClick={() => copyToClipboard(regDocInfo.number, 'regNo')}
                        className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors shrink-0"
                        title="Copy Reg Number"
                      >
                        {copiedField === 'regNo' ? (
                          <CheckIcon className="h-4 w-4 text-green-500 animate-in zoom-in duration-200" />
                        ) : (
                          <DocumentDuplicateIcon className="h-4 w-4" />
                        )}
                      </button>
                    )}
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <div className="min-w-0 pr-2">
                      <span className="text-gray-500 dark:text-gray-400">Issued: </span>
                      <span className="font-semibold text-gray-900 dark:text-white block">{regDocInfo.issuedDate}</span>
                    </div>
                    {regDocInfo.issuedDate !== 'N/A' && (
                      <button
                        type="button"
                        onClick={() => copyToClipboard(regDocInfo.issuedDate, 'issuedDate')}
                        className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-500 hover:text-gray-900 dark:hover:text-white transition-colors shrink-0"
                        title="Copy Issue Date"
                      >
                        {copiedField === 'issuedDate' ? (
                          <CheckIcon className="h-4 w-4 text-green-500 animate-in zoom-in duration-200" />
                        ) : (
                          <DocumentDuplicateIcon className="h-4 w-4" />
                        )}
                      </button>
                    )}
                  </div>

                  {regDocInfo.url && (
                    <div className="pt-2">
                      <button
                        type="button"
                        onClick={() => {
                          setPreviewUrl(regDocInfo.url);
                          setPreviewLoading(true);
                        }}
                        className="flex w-full items-center justify-center gap-2 px-3 py-2 text-xs font-semibold rounded-xl border border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-all dark:border-gray-700 dark:bg-gray-800/50 dark:text-gray-200 dark:hover:bg-gray-700"
                      >
                        <EyeIcon className="h-4 w-4 text-blue-600 dark:text-blue-400 shrink-0" />
                        View Registration Certificate
                      </button>
                    </div>
                  )}
                </div>

                <div className="pt-3 space-y-2">
                  <h3 className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Recommended Councils</h3>
                  <div className="space-y-1.5">
                    {recommendedCouncils.map((c) => (
                      <a
                        key={c.id}
                        href={c.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between p-2 rounded-xl bg-green-50/20 hover:bg-green-50 text-green-800 dark:bg-green-950/10 dark:hover:bg-green-950/20 dark:text-green-300 border border-green-500/50 dark:border-green-700/50 transition-all group"
                      >
                        <div className="flex items-center gap-2">
                          <CheckCircleIcon className="h-4 w-4 text-green-600 dark:text-green-400 shrink-0" />
                          <span className="text-xs font-semibold">{c.name}</span>
                        </div>
                        <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 text-green-600 dark:text-green-400 opacity-60 group-hover:opacity-100 transition-opacity" />
                      </a>
                    ))}
                  </div>
                </div>

                <div className="pt-3 space-y-2">
                  <h3 className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Other Registries</h3>
                  <div className="space-y-1.5 max-h-[160px] overflow-y-auto pr-1">
                    {otherCouncils.map((c) => (
                      <a
                        key={c.id}
                        href={c.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between p-2 rounded-xl bg-gray-50 hover:bg-gray-100 dark:bg-gray-700/30 dark:hover:bg-gray-700/50 text-gray-700 dark:text-gray-300 border border-gray-100 dark:border-gray-800 transition-all group"
                      >
                        <span className="text-xs font-medium">{c.name}</span>
                        <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-200 transition-colors" />
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-900 dark:text-white border-b dark:border-gray-700 pb-2">
                Basic Information
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <ValueRow label="Unique ID" value={data.generatedId} />
                <ValueRow label="Onboard Date & Time" value={formatDateTime(data.vendor?.createdAt)} />
                <ValueRow label="Category" value={data.category} />
                <ValueRow label="Clinic Name" value={data.clinicName} />
                <ValueRow label="Doctor Name" value={data.doctorName} />
                <ValueRow
                  label="Medical Registration Council"
                  value={data.medicalRegistrationCouncil || data.registrationCouncil}
                />
                <ValueRow label="Registration Number" value={regDocInfo.number} />
                <ValueRow label="Registration Issue Date" value={regDocInfo.issuedDate} />
                <ValueRow label="Gender" value={data.gender} />
                <ValueRow label="Phone Number" value={data.phoneNumber} />
                <ValueRow label="Patient Contact Number" value={data.patientContactNumber} />
                <ValueRow label="Show Patient Contact Number" value={formatPrimitive(data.showPatientContactNumber)} />
                <ValueRow label="Email" value={data.email} />
                <ValueRow label="Telemedicine Experience" value={formatPrimitive(data.hasTelemedicineExperience)} />
                <ValueRow label="Doctor About" value={data.doctorAbout} />
                <ValueRow
                  label="Experience"
                  value={
                    data.experienceYears == null ? 'N/A' : `${data.experienceYears} years`
                  }
                />
              </div>
            </div>

            <div className="space-y-4">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-900 dark:text-white border-b dark:border-gray-700 pb-2">
                Consultation & Clinic
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <ValueRow label="Establishment Proof" value={data.establishmentProof} />
                <ValueRow label="Nearby Landmark" value={data.nearbyLandmark} />
                <ValueRow label="Floor" value={data.floor} />
                <ValueRow
                  label="Commission Rate"
                  value={
                    data.commissionRate == null
                      ? 'N/A'
                      : `${data.commissionRate}% (On fees)`
                  }
                />
                <ValueRow
                  label="Referral Commission"
                  value={
                    data.referralCommission == null
                      ? 'N/A'
                      : `₹${data.referralCommission} (On billing amount)`
                  }
                />
              </div>
              <div className="pt-2">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Accessibility</p>
                <div className="flex flex-wrap gap-2">
                  {data.hasLiftAccess && <span className="px-2 py-1 rounded-full text-xs bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">Lift Access</span>}
                  {data.hasWheelchairAccess && <span className="px-2 py-1 rounded-full text-xs bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300">Wheelchair Access</span>}
                  {data.hasParking && <span className="px-2 py-1 rounded-full text-xs bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">Parking Available</span>}
                  {!data.hasLiftAccess && !data.hasWheelchairAccess && !data.hasParking && (
                    <span className="text-sm text-gray-500 dark:text-gray-400">N/A</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Qualifications</h3>
              <Chips items={educationalQualifications} />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white pt-2">Specializations</h3>
              <Chips items={specializations} />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white pt-2">Languages</h3>
              <Chips items={languageProficiency} />
            </div>
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Consultation Types</h3>
              <Chips items={consultationTypes} />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white pt-2">Consultation Days</h3>
              <Chips items={consultationDaysOrdered} />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white pt-2">Insurance Partners</h3>
              <Chips items={insurancePartners} />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white pt-2">Other Facilities</h3>
              <Chips items={otherFacilities} />
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b dark:border-gray-700 pb-2">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-900 dark:text-white">
                Speciality-wise fees
              </h2>
              <button
                type="button"
                onClick={openAddFeeModal}
                className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700"
              >
                <PlusIcon className="h-4 w-4" />
                Add fees
              </button>
            </div>
            {specialityFeeRows.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">No speciality fees added yet.</p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="w-full min-w-[760px] text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50 text-left dark:border-gray-700 dark:bg-gray-900/40">
                      <th className="whitespace-nowrap py-3 px-4 font-semibold text-gray-700 dark:text-gray-200">
                        Speciality
                      </th>
                      {showOnlineFeeColumn ? (
                        <th className="whitespace-nowrap py-3 px-4 font-semibold text-gray-700 dark:text-gray-200">
                          Online
                        </th>
                      ) : null}
                      {showOfflineFeeColumn ? (
                        <th className="whitespace-nowrap py-3 px-4 font-semibold text-gray-700 dark:text-gray-200">
                          In-clinic
                        </th>
                      ) : null}
                      <th className="whitespace-nowrap py-3 px-4 font-semibold text-gray-700 dark:text-gray-200">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {specialityFeeRows.map((row) => (
                      <tr
                        key={row.id || row.specialityId}
                        className="border-b border-gray-100 dark:border-gray-800 last:border-0"
                      >
                        <td className="whitespace-nowrap py-3 px-4 font-medium text-gray-900 dark:text-white">
                          {row.speciality?.trim() || 'N/A'}
                        </td>
                        {showOnlineFeeColumn ? (
                          <td className="py-3 px-4 text-gray-700 dark:text-gray-300">
                            {formatFeePairCell(row.feesByConsultationType?.online)}
                          </td>
                        ) : null}
                        {showOfflineFeeColumn ? (
                          <td className="py-3 px-4 text-gray-700 dark:text-gray-300">
                            {formatFeePairCell(row.feesByConsultationType?.offline)}
                          </td>
                        ) : null}
                        <td className="whitespace-nowrap py-3 px-4">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => openEditFeeModal(row)}
                              className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
                            >
                              <PencilSquareIcon className="h-3.5 w-3.5" />
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteFee(row)}
                              disabled={!row.id || feeActionLoadingId === row.id}
                              className="inline-flex items-center gap-1 rounded-md border border-red-200 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-60 dark:border-red-900/50 dark:text-red-300 dark:hover:bg-red-900/20"
                            >
                              <TrashIcon className="h-3.5 w-3.5" />
                              {feeActionLoadingId === row.id ? 'Deleting...' : 'Delete'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <DoctorClinicSpecialityFeeEditorModal
            open={feeModalOpen}
            mode={feeModalMode}
            vendorId={vendorId || ''}
            consultationTypes={consultationTypes}
            specialityOptions={specialityOptions}
            existingFeeRows={specialityFeeRows}
            editingRow={editingFeeRow}
            onClose={() => setFeeModalOpen(false)}
            onSaved={reloadProfile}
          />
          <ConfirmDialog
            open={feeDeleteTarget != null}
            title="Delete speciality fees?"
            message={
              feeDeleteTarget
                ? `Fees for ${feeDeleteTarget.speciality?.trim() || 'this speciality'} will be permanently removed. This cannot be undone.`
                : ''
            }
            tone="danger"
            confirmLabel="Delete"
            busy={feeActionLoadingId != null}
            onCancel={() => {
              if (!feeActionLoadingId) setFeeDeleteTarget(null);
            }}
            onConfirm={() => void confirmDeleteFee()}
          />
          <Toaster position="top-right" />

          <div className="space-y-6">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-900 dark:text-white border-b dark:border-gray-700 pb-2">
              Clinic locations & timeslots
            </h2>

            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                Offline — clinic locations & timeslots
              </h3>
              {clinicBranches.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">No clinic branches added.</p>
              ) : (
                <div className="space-y-4">
                  {clinicBranches.map((branch: DoctorClinicBranch) => (
                    <div
                      key={branch.clinicBranchId}
                      className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-2 bg-gray-50 px-4 py-3 dark:bg-gray-900/40">
                        <div>
                          <p className="font-semibold text-gray-900 dark:text-white">
                            {branch.clinicName?.trim() || 'Unnamed clinic'}
                          </p>
                          <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-400">
                            {branch.address?.trim() || 'N/A'}
                            {branch.pincode ? ` — ${branch.pincode}` : ''}
                          </p>
                        </div>
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-semibold ${branch.isActive
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                              : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
                            }`}
                        >
                          {branch.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      {(branch.timeslots || []).length === 0 ? (
                        <p className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                          No offline timeslots configured for this clinic.
                        </p>
                      ) : (
                        <TimeslotsTable timeslots={branch.timeslots} />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                Online — video consultation timeslots
              </h3>
              {onlineTimeslots.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No online timeslots configured for this doctor.
                </p>
              ) : (
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <TimeslotsTable timeslots={onlineTimeslots} />
                </div>
              )}
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between border-b dark:border-gray-700 pb-2">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-900 dark:text-white">
                Uploaded Documents
              </h2>
              <button
                type="button"
                onClick={handleOpenAddModal}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow-sm transition-colors"
              >
                <PlusIcon className="h-4 w-4" />
                Upload Document
              </button>
            </div>
            {vendorDocuments.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">N/A</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left border-b border-gray-200 dark:border-gray-700">
                      <th className="py-2 pr-4">Type</th>
                      <th className="py-2 pr-4">Name</th>
                      <th className="py-2 pr-4">Number</th>
                      <th className="py-2 pr-4">Issued</th>
                      <th className="py-2 pr-4">Expiry</th>
                      <th className="py-2 pr-4">Verified</th>
                      <th className="py-2 pr-4 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vendorDocuments.map((doc) => (
                      <tr key={doc.documentId} className="border-b border-gray-100 dark:border-gray-800">
                        <td className="py-2 pr-4">{doc.documentType?.trim() ? doc.documentType : 'N/A'}</td>
                        <td className="py-2 pr-4">{doc.documentName?.trim() ? doc.documentName : 'N/A'}</td>
                        <td className="py-2 pr-4">{doc.documentNumber || 'N/A'}</td>
                        <td className="py-2 pr-4">{doc.issuedDate || 'N/A'}</td>
                        <td className="py-2 pr-4">
                          {doc.isLifetime ? 'Lifetime' : doc.expiryDate || 'N/A'}
                        </td>
                        <td className="py-2 pr-4">
                          <span
                            className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${doc.isVerified
                                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
                                : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                              }`}
                          >
                            {doc.isVerified ? 'Verified' : 'Not verified'}
                          </span>
                        </td>
                        <td className="py-2 pl-2 pr-1 text-right">
                          <button
                            ref={(el) => {
                              docMenuButtonRefs.current[doc.documentId] = el;
                            }}
                            type="button"
                            aria-label="Document actions"
                            className="inline-flex items-center justify-center rounded-md p-1.5 text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white"
                            onClick={() =>
                              setActiveDocMenuId((prev) => (prev === doc.documentId ? null : doc.documentId))
                            }
                          >
                            <EllipsisVerticalIcon className="h-5 w-5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-900 dark:text-white border-b dark:border-gray-700 pb-2">
              Accepted Policy
            </h2>
            {acceptedPolicies.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">N/A</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left border-b border-gray-200 dark:border-gray-700">
                      <th className="py-2 pr-4">Policy Type</th>
                      <th className="py-2 pr-4">Accepted Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {acceptedPolicies.map((policy) => (
                      <tr key={policy.id} className="border-b border-gray-100 dark:border-gray-800">
                        <td className="py-2 pr-4">{policy.policyType?.trim() ? policy.policyType : 'N/A'}</td>
                        <td className="py-2 pr-4">{formatDateTime(policy.acceptedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Clinic Photos</h3>
            {clinicPhotos.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">N/A</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {clinicPhotos.map((photo, index) => (
                  <FilePreview key={`${photo.url}-${index}`} url={photo.url} name={photo.name} className="w-full" />
                ))}
              </div>
            )}
          </div>

          {shouldShowMapLocation && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Clinic Location Map</h3>
              <div className="w-full h-72 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                <iframe
                  title="Clinic location map"
                  src={`https://maps.google.com/maps?q=${encodeURIComponent(
                    (data.location && String(data.location).trim()) ||
                    `${data.address || ''}, ${data.city || ''}, ${data.state || ''} ${data.pincode || ''}`
                  )}&z=15&output=embed`}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          )}
        </div>
      </section>

      {activeDocMenuId && (() => {
        const activeDoc = vendorDocuments.find((d) => d.documentId === activeDocMenuId);
        if (!activeDoc) return null;
        return (
          <DocActionMenu
            doc={activeDoc}
            getAnchorEl={() => docMenuButtonRefs.current[activeDocMenuId] ?? null}
            open
            loading={documentActionLoadingId === activeDocMenuId}
            onView={() => {
              handleViewDocument(activeDoc.documentUrl);
              setActiveDocMenuId(null);
            }}
            onReplace={() => handleOpenReplaceModal(activeDoc)}
            onVerifyToggle={() => void handleVerifyToggle(activeDoc)}
            onEditExpiry={() => handleOpenExpiryEdit(activeDoc)}
            onClose={() => setActiveDocMenuId(null)}
          />
        );
      })()}

      {/* Expiry Date Edit Modal */}
      {expiryEditDoc && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4 animate-in zoom-in-95 duration-200">
            <div>
              <h3 className="text-base font-bold text-gray-900 dark:text-white">Edit Expiry Date</h3>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                {expiryEditDoc.documentType} — {expiryEditDoc.documentName}
              </p>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Issued Date</label>
                <input
                  type="date"
                  value={expiryEditIssued}
                  onChange={(e) => setExpiryEditIssued(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Expiry Date
                  {expiryEditDoc.documentType === 'REGISTRATION_CERTIFICATE' && (
                    <span className="ml-1 text-red-500">*</span>
                  )}
                </label>
                <input
                  type="date"
                  value={expiryEditDate}
                  onChange={(e) => setExpiryEditDate(e.target.value)}
                  disabled={expiryIsLifetime}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                />
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={expiryIsLifetime}
                  onChange={(e) => {
                    setExpiryIsLifetime(e.target.checked);
                    if (e.target.checked) setExpiryEditDate('');
                  }}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Lifetime (no expiry)</span>
              </label>

              {!expiryIsLifetime && expiryEditDoc.documentType === 'REGISTRATION_CERTIFICATE' && !expiryEditDate && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  ⚠ Registration Certificate requires an expiry date.
                </p>
              )}
              {!expiryIsLifetime && String(expiryEditDoc.documentName || '').toLowerCase().includes('driving') && !expiryEditDate && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  ⚠ Driving License requires an expiry date.
                </p>
              )}
            </div>

            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={() => setExpiryEditDoc(null)}
                disabled={expiryEditLoading}
                className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleSaveExpiry()}
                disabled={expiryEditLoading || (!expiryIsLifetime && !expiryEditDate)}
                className="flex-1 rounded-lg bg-indigo-600 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60 transition-colors"
              >
                {expiryEditLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {vendorId && (
        <AdminUploadDocumentModal
          isOpen={isUploadModalOpen}
          onClose={() => setIsUploadModalOpen(false)}
          vendorId={vendorId}
          vendorType={2}
          existingDocuments={vendorDocuments}
          initialDocument={selectedDocForEdit}
          onSuccess={handleDocumentUploadSuccess}
        />
      )}

      {previewUrl && (
        <div className="fixed inset-0 z-[250] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col animate-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="flex justify-between items-center p-4 border-b dark:border-gray-700 shrink-0 bg-white dark:bg-gray-800">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  Registration Certificate Preview
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Reg No: {regDocInfo.number}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  title="Open in new tab"
                >
                  <ArrowTopRightOnSquareIcon className="w-5 h-5" />
                </a>
                <button
                  onClick={() => setPreviewUrl(null)}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  title="Close"
                >
                  <XCircleIcon className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content Container */}
            <div className="flex-1 overflow-auto p-4 relative min-h-[300px] flex items-center justify-center bg-gray-50 dark:bg-gray-900/50">
              {previewLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm z-10">
                  <div className="flex flex-col items-center">
                    <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600" />
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Loading certificate...</p>
                  </div>
                </div>
              )}

              {isImage(previewUrl) ? (
                <img
                  src={previewUrl}
                  alt="Registration Certificate"
                  className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-md"
                  onLoad={() => setPreviewLoading(false)}
                  onError={() => setPreviewLoading(false)}
                />
              ) : isPDF(previewUrl) ? (
                <div className="w-full h-[70vh]">
                  <iframe
                    src={`${previewUrl}#view=fit`}
                    className="w-full h-full border-0 rounded-lg shadow-md bg-white"
                    title="Registration Certificate PDF"
                    onLoad={() => setPreviewLoading(false)}
                  />
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    This file format cannot be previewed directly.
                  </p>
                  <a
                    href={previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors text-sm font-semibold"
                  >
                    Open Certificate in New Tab
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {data ? (
        <DoctorClinicWhatsAppSendModal
          clinic={data}
          open={whatsappSendOpen}
          onClose={() => setWhatsappSendOpen(false)}
        />
      ) : null}

      {digitalCardUrl && (
        <div className="fixed inset-0 z-[250] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col animate-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="flex justify-between items-center p-4 border-b dark:border-gray-700 shrink-0 bg-white dark:bg-gray-800">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  Doctor Digital Card
                </h3>
              </div>
              <div className="flex items-center gap-2">
                {/* Zoom Controls */}
                <div className="flex items-center gap-1.5 bg-gray-100 dark:bg-gray-700 p-1 rounded-xl mr-2">
                  <button
                    onClick={() => setCardZoom(prev => Math.max(0.5, prev - 0.2))}
                    className="px-2 py-1 text-xs font-bold text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                    title="Zoom Out"
                  >
                    -
                  </button>
                  <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 w-12 text-center">
                    {Math.round(cardZoom * 100)}%
                  </span>
                  <button
                    onClick={() => setCardZoom(prev => Math.min(3, prev + 0.2))}
                    className="px-2 py-1 text-xs font-bold text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                    title="Zoom In"
                  >
                    +
                  </button>
                  <button
                    onClick={() => {
                      setCardZoom(1);
                      setCardPosition({ x: 0, y: 0 });
                    }}
                    className="px-1.5 py-1 text-xs font-semibold text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg transition-colors"
                    title="Reset"
                  >
                    Reset
                  </button>
                </div>

                {/* Download Button */}
                <button
                  onClick={handleDownloadCard}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl shadow-sm transition-colors mr-2"
                  title="Download Image"
                >
                  <ArrowDownTrayIcon className="w-4 h-4" />
                  Download
                </button>
                <button
                  onClick={() => setDigitalCardUrl(null)}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  title="Close"
                >
                  <XCircleIcon className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Image Container with Drag & Zoom */}
            <div
              className="flex-1 overflow-hidden p-6 flex items-center justify-center bg-gray-50 dark:bg-gray-900/50 cursor-grab active:cursor-grabbing relative select-none"
              onMouseDown={(e) => {
                e.preventDefault();
                setIsDraggingCard(true);
                setDragStartCard({ x: e.clientX - cardPosition.x, y: e.clientY - cardPosition.y });
              }}
              onMouseMove={(e) => {
                if (!isDraggingCard) return;
                setCardPosition({
                  x: e.clientX - dragStartCard.x,
                  y: e.clientY - dragStartCard.y
                });
              }}
              onMouseUp={() => setIsDraggingCard(false)}
              onMouseLeave={() => setIsDraggingCard(false)}
            >
              <img
                src={digitalCardUrl}
                alt="Doctor Digital Card"
                draggable="false"
                className="max-w-full max-h-[65vh] object-contain rounded-lg shadow-lg border border-gray-100 dark:border-gray-800"
                style={{
                  transform: `translate(${cardPosition.x}px, ${cardPosition.y}px) scale(${cardZoom})`,
                  transformOrigin: 'center center',
                  transition: isDraggingCard ? 'none' : 'transform 0.1s ease-out',
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DoctorClinicVendorDetailsPage;
